// @flow
import React from 'react';
import ga from 'app/common/lib/ga';
import i18n from 'app/common/lib/i18n';
import LinkPogWrapper from 'app/common/react/ui/experimental/LinkPogWrapper';
import WebPushManager from 'app/common/lib/WebPushManager';
import { useHeaderHeight } from 'app/common/react/components/Header/Context/ContextConsumer';
import { Box, Text, Button, IconButton, Sticky } from 'gestalt';

type BannerProps = {|
  color?: 'blue' | 'darkGray',
  completeButtonText?: string,
  completeUrl?: string,
  dismissButtonText?: ?string,
  experienceId?: number,
  message: string,
  onComplete?: (event: SyntheticMouseEvent<>) => void,
  onDismiss?: (event: SyntheticMouseEvent<>) => void,
  onRender?: () => void,
  useNewTab?: boolean,
|};

const subscribeToChromePush = () => WebPushManager.instance().subscribe();

export default function Banner(props: BannerProps) {
  const {
    dismissButtonText,
    color,
    completeButtonText,
    completeUrl,
    experienceId,
    message,
    onComplete,
    onDismiss,
    onRender,
    useNewTab,
  } = props;

  if (onRender) {
    onRender();
  }

  // TODO(juliac): Update chrome push nag copy in ehq
  const handleComplete = ({ event }: { event: SyntheticMouseEvent<> }) => {
    if (experienceId === PCONST.thrift.Experience.WEB_NAG_CHROME_PUSH) {
      subscribeToChromePush();
    }
    if (onComplete) {
      onComplete(event);
    }
  };

  const handleDismiss = ({ event }: { event: SyntheticMouseEvent<> }) => {
    ga.trackEvent('nag', PCONST.GA_ACTION_CLICK_CLOSE);

    if (onDismiss) {
      onDismiss(event);
    }
  };

  const legacySchema = message && !completeButtonText;

  // If this is GDPR experience on web, default to completeButton with no url redirect (used for mobile experiences).
  // We show GDPR modals on top of homefeed.
  const isGdprExperience =
    experienceId === PCONST.thrift.Experience.EUROPEAN_UNION_PARENTAL_APPROVAL_NAG;

  const completeButton = completeButtonText &&
    (!completeUrl || isGdprExperience) && (
      <Button
        color="white"
        inline
        onClick={handleComplete}
        text={completeButtonText}
        textColor={color}
      />
    );

  const completeLink = completeButtonText &&
    completeUrl && (
      <LinkPogWrapper
        accessibilityLabel={completeButtonText}
        key="naglink"
        shape="rounded"
        url={completeUrl}
        onClick={(event) => handleComplete({ event })}
        useNewTab={useNewTab}
      >
        <Box
          color="white"
          dangerouslySetInlineStyle={{
            __style: { padding: '11px 14px', borderRadius: 8, minHeight: 40 },
          }}
        >
          <Text align="center" color={color} weight="bold">
            {completeButtonText}
          </Text>
        </Box>
      </LinkPogWrapper>
    );

  const dismissButton = dismissButtonText &&
    onDismiss && (
      <Box mdPaddingX={2}>
        <Button inline onClick={handleDismiss} text={dismissButtonText} color="transparent" />
      </Box>
    );

  const closeButton = legacySchema &&
    !isGdprExperience &&
    onDismiss && (
      <IconButton
        accessibilityLabel={i18n._('Close', 'accessibility close label')}
        bgColor="lightGray"
        onClick={handleDismiss}
        icon="cancel"
      />
    );
  const headerHeight = useHeaderHeight();

  // using NagBase and message classes to handle link styles for legacy schema's
  return (
    <Sticky dangerouslySetZIndex={{ __zIndex: 2 }} top={headerHeight}>
      <div className="NagBase">
        <Box
          xs={{ display: 'flexColumn' }}
          sm={{ display: 'flex' }}
          color={color || 'darkGray'}
          paddingY={3}
          paddingX={6}
          alignItems="center"
          justifyContent="between"
        >
          <Box xs={{ column: 12 }} sm={{ column: !legacySchema ? 8 : 11 }}>
            <Text color="white" weight={!isGdprExperience ? 'bold' : 'normal'}>
              {/* Need to have this for the NoscriptNag and older Experience Nags */}
              <div className="message">
                {/* eslint-disable react/no-danger */}
                <span dangerouslySetInnerHTML={{ __html: message }} />
                {/* eslint-enable react/no-danger */}
              </div>
            </Text>
          </Box>
          <Box display="flex" alignItems="center" justifyContent="end">
            {dismissButton}
            {completeButton || completeLink}
            {closeButton}
          </Box>
        </Box>
      </div>
    </Sticky>
  );
}
